import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, Users } from 'lucide-react';

export default function ArtikelGeschenkeFamilie() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"><ArrowLeft className="w-4 h-4" />Alle Ratgeber</Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4"><Calendar className="w-4 h-4" /><span>Februar 2026</span></div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">Eid Geschenke für Familienmitglieder: Ideen für alle Generationen</h1>
          <p className="text-lg text-white/80 leading-relaxed">Von Großeltern bis Enkelkinder – passende Geschenkideen für jedes Familienmitglied.</p>
        </div>
      </section>
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 prose prose-lg max-w-none">
          <p className="text-xl text-[#2C332E] leading-relaxed mb-8">Eid ist vor allem ein Familienfest. Die Kunst ist, für jedes Familienmitglied das passende Geschenk zu finden – unabhängig von Alter und Geschmack. Dieser Guide hilft dir, für Großeltern, Eltern, Geschwister und Kinder das Richtige auszuwählen.</p>

          <div className="space-y-8 mb-12">
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center"><Users className="w-6 h-6 text-[#4A5D4F]" /></div>
                <h2 className="font-serif text-2xl font-medium text-[#2C332E]">Für Großeltern</h2>
              </div>
              <p className="text-[#4B5563] mb-4">Die Generation, die uns die Traditionen beigebracht hat – sie verdienen besondere Wertschätzung.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332E] mb-2">Für Oma</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Seiden-Hijab oder Gebetskleidung</li>
                    <li>• Quran mit großer Schrift</li>
                    <li>• Digitaler Quran-Player</li>
                    <li>• Foto-Album mit Familienbildern</li>
                    <li>• Duftende Körperpflege</li>
                  </ul>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332E] mb-2">Für Opa</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Hochwertige Tasbih</li>
                    <li>• Bequemer Gebetsteppich</li>
                    <li>• Attar/Parfümöl</li>
                    <li>• Islamische Bücher</li>
                    <li>• Kufi/Gebetsmütze</li>
                  </ul>
                </div>
              </div>
              <div className="bg-[#4A5D4F]/10 rounded-lg p-4 mt-4">
                <p className="text-sm text-[#4B5563]"><strong>Tipp:</strong> Zeit ist das wertvollste Geschenk! Ein gemeinsamer Ausflug oder Besuch bedeutet Großeltern oft mehr als materielle Dinge.</p>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center"><Users className="w-6 h-6 text-[#4A5D4F]" /></div>
                <h2 className="font-serif text-2xl font-medium text-[#2C332E]">Für Eltern</h2>
              </div>
              <p className="text-[#4B5563] mb-4">Sie haben während des Ramadan gekocht, organisiert und die Familie zusammengehalten.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332E] mb-2">Für Mama</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Parfüm-Geschenkset</li>
                    <li>• Spa-/Wellness-Gutschein</li>
                    <li>• Schmuck mit Bedeutung</li>
                    <li>• Haushaltshelfer (Thermomix etc.)</li>
                    <li>• Restaurant-Gutschein (einmal nicht kochen!)</li>
                  </ul>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332E] mb-2">Für Papa</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Hochwertige Uhr</li>
                    <li>• Oud-Parfüm</li>
                    <li>• Leder-Accessoires</li>
                    <li>• Technik-Gadgets</li>
                    <li>• Erlebnis-Gutschein</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center"><Users className="w-6 h-6 text-[#4A5D4F]" /></div>
                <h2 className="font-serif text-2xl font-medium text-[#2C332E]">Für Geschwister</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332E] mb-2">Für die Schwester</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Hijab-Set oder Accessoires</li>
                    <li>• Beauty-Produkte</li>
                    <li>• Personalisierter Schmuck</li>
                    <li>• Gutschein (Mode, Beauty)</li>
                    <li>• Gemeinsame Aktivität</li>
                  </ul>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332E] mb-2">Für den Bruder</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Sneaker oder Kleidung</li>
                    <li>• Gaming-Zubehör</li>
                    <li>• Parfüm</li>
                    <li>• Sport-Equipment</li>
                    <li>• Technik-Gadgets</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center"><Users className="w-6 h-6 text-[#4A5D4F]" /></div>
                <h2 className="font-serif text-2xl font-medium text-[#2C332E]">Für Nichten & Neffen</h2>
              </div>
              <div className="space-y-4">
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332E] mb-2">Kleinkinder (0-5)</h4>
                  <p className="text-sm text-[#4B5563]">Plüschtiere, Stoffbücher, Spielzeug, Kleidung, Eidi (5-10€)</p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332E] mb-2">Schulkinder (6-12)</h4>
                  <p className="text-sm text-[#4B5563]">Islamische Bücher, Spiele, Bastelsets, Spielzeug, Eidi (10-30€)</p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332E] mb-2">Teenager (13+)</h4>
                  <p className="text-sm text-[#4B5563]">Gutscheine, Technik, Mode, Bücher, Eidi (20-50€)</p>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center"><Users className="w-6 h-6 text-[#4A5D4F]" /></div>
                <h2 className="font-serif text-2xl font-medium text-[#2C332E]">Für Schwiegereltern</h2>
              </div>
              <p className="text-[#4B5563] mb-4">Zeige Respekt und Wertschätzung mit durchdachten Geschenken.</p>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <ul className="text-sm text-[#4B5563] space-y-2">
                  <li>• Hochwertige Datteln in Geschenkbox</li>
                  <li>• Parfüm oder Attar</li>
                  <li>• Schöner Gebetsteppich</li>
                  <li>• Islamische Wandkunst</li>
                  <li>• Einladung zum Eid-Essen</li>
                </ul>
              </div>
            </section>
          </div>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Budget-Übersicht pro Person</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#4A5D4F] text-white">
                  <tr><th className="p-3 text-left">Familienmitglied</th><th className="p-3 text-left">Empfohlenes Budget</th></tr>
                </thead>
                <tbody className="text-[#4B5563]">
                  <tr className="border-b"><td className="p-3">Eltern</td><td className="p-3">50-150 €</td></tr>
                  <tr className="border-b bg-[#F9FAFB]"><td className="p-3">Großeltern</td><td className="p-3">30-100 €</td></tr>
                  <tr className="border-b"><td className="p-3">Geschwister</td><td className="p-3">20-80 €</td></tr>
                  <tr className="border-b bg-[#F9FAFB]"><td className="p-3">Schwiegereltern</td><td className="p-3">40-100 €</td></tr>
                  <tr className="border-b"><td className="p-3">Nichten/Neffen</td><td className="p-3">10-50 €</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Fazit</h2>
            <p className="text-[#4B5563] leading-relaxed mb-4">Das perfekte Familiengeschenk zeigt Wertschätzung und Kenntnis der Person. Plane früh, denke an jeden – und vergiss nicht: Zeit und Aufmerksamkeit sind oft wertvoller als teure Geschenke!</p>
            <p className="text-xl text-[#4A5D4F] font-medium">Eid Mubarak! 🌙</p>
          </section>

          <div className="bg-[#F5F5F0] rounded-2xl p-6 mb-8">
            <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-4">Weitere Artikel</h3>
            <ul className="space-y-2">
              <li><Link to="/ratgeber/25-geschenke-kinder" className="text-[#4A5D4F] hover:underline">→ 25 beste Eid Geschenke für Kinder</Link></li>
              <li><Link to="/ratgeber/15-geschenke-frauen" className="text-[#4A5D4F] hover:underline">→ 15 Geschenke für Frauen</Link></li>
              <li><Link to="/ratgeber/10-geschenke-maenner" className="text-[#4A5D4F] hover:underline">→ 10 Geschenke für Männer</Link></li>
            </ul>
          </div>

          <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
            <h3 className="font-serif text-2xl font-medium text-white mb-4">Geschenke entdecken</h3>
            <Link to="/geschenke" className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors">Jetzt stöbern<Gift className="w-5 h-5" /></Link>
          </div>
        </div>
      </article>
    </main>
  );
}
